import PropTypes from 'prop-types';
import { OverlayBlocks } from '@components/page/BlockRenderer';
import { FADE_DURATION } from '@components/shared/ImageViewer/imageViewerUtils';
import { slideshowSettingsOf, slideshowTextPage, wrapTextBlocksInHighlight } from '@/slideshow/text';

/**
 * The slideshow's text, wherever the slideshow shows it. One store (the
 * page's Text and Vector blocks), drawn in the frame of its placement:
 * - panel: bottom-left over the images on a wide screen
 * - corner: pinned bottom-left on a tall screen
 * - slide: the text slide on a phone or in the phone layout
 * - intro: the full-screen intro before the images
 * - empty: a slideshow with no images yet
 */
export default function SlideshowText({
    page,
    pages,
    files,
    theme,
    placement,
    landscape = false,
    fill = false,
}) {
    const textPage = slideshowTextPage(page);
    const settings = slideshowSettingsOf(page);
    const overBackdrop = placement === 'panel' || placement === 'corner';
    const blocks = (
        <OverlayBlocks
            page={textPage}
            pages={pages}
            files={files}
            theme={theme}
            gallery="slideshow"
            displayHtml={overBackdrop ? wrapTextBlocksInHighlight : null}
        />
    );
    const color = { color: 'var(--color-text)' };
    const frame = {
        'data-slideshow-text': placement,
        'data-slideshow-type': settings.type === 'body' ? undefined : settings.type,
    };

    if (placement === 'panel') {
        return (
            <div
                {...frame}
                className="slideshow-text slideshow-backdrop absolute left-0 bottom-0 z-10 w-[32%] max-h-[90vh] overflow-hidden pointer-events-auto bg-transparent"
                style={{
                    minWidth: `${settings.desktop.width.min}ch`,
                    maxWidth: `${settings.desktop.width.max}ch`,
                    '--slideshow-backdrop': `${settings.desktop.backdrop}%`,
                }}
            >
                <aside className="w-full h-full overflow-y-auto px-[1.6em] py-8 flex flex-col justify-end" style={color}>
                    {blocks}
                </aside>
            </div>
        );
    }

    if (placement === 'corner') {
        return (
            <div
                {...frame}
                className="slideshow-text slideshow-backdrop fixed bottom-[20px] md:bottom-[19px] left-[20px] md:left-[30px] pointer-events-auto max-h-[40vh] overflow-y-auto z-10"
                style={{ '--slideshow-backdrop': `${settings.desktop.backdrop}%` }}
            >
                <aside className="flex-1 min-h-[200px] max-h-[35%] overflow-y-auto p-8" style={color}>
                    {blocks}
                </aside>
            </div>
        );
    }

    if (placement === 'slide') {
        const top = settings.phone.align === 'top';
        const layout = landscape
            ? 'px-8 py-4 text-[0.8rem]'
            : 'px-6 pt-12 pb-6 text-[0.7rem]';
        return (
            <div
                {...frame}
                className={`slideshow-text ${fill ? 'absolute inset-0 ' : ''}w-full h-full flex ${top ? 'items-start' : 'items-center'} justify-center bg-background ${layout}`}
                style={{ transition: `background-color ${FADE_DURATION}ms ease-in-out`, ...color }}
            >
                <article className="w-full max-h-full overflow-y-auto">
                    {blocks}
                </article>
            </div>
        );
    }

    // intro and empty
    return (
        <article {...frame} className="slideshow-text max-w-[65ch] w-full" style={color}>
            {blocks}
        </article>
    );
}

SlideshowText.propTypes = {
    page: PropTypes.object,
    pages: PropTypes.object,
    files: PropTypes.object,
    theme: PropTypes.object,
    placement: PropTypes.oneOf(['panel', 'corner', 'slide', 'intro', 'empty']).isRequired,
    landscape: PropTypes.bool,
    /** Fill the positioned parent (the phone layout without the swiper). */
    fill: PropTypes.bool,
};
